var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { buildDataZoom, buildLegend, buildTooltip, mergeCategorySettings, } from "./shared";
var GRID_SIDE_PADDING = 60;
var AXIS_OFFSET_STEP = 50;
var DEFAULT_COLORS = [
    "#5470C6",
    "#91CC75",
    "#EE6666",
    "#FAC858",
    "#73C0DE",
    "#3BA272",
    "#FC8452",
    "#9A60B4",
    "#EA7CCC",
];
function hexToRgba(hex, alpha) {
    if (!hex.startsWith("#")) {
        return undefined;
    }
    var normalized = hex.length === 4
        ? "#".concat(hex[1]).concat(hex[1]).concat(hex[2]).concat(hex[2]).concat(hex[3]).concat(hex[3])
        : hex;
    var bigint = parseInt(normalized.slice(1), 16);
    if (Number.isNaN(bigint)) {
        return undefined;
    }
    var r = (bigint >> 16) & 255;
    var g = (bigint >> 8) & 255;
    var b = bigint & 255;
    return "rgba(".concat(r, ", ").concat(g, ", ").concat(b, ", ").concat(alpha, ")");
}
var DEFAULT_AXIS_ID = "left";
function pickSeriesType(specificType, chartType, settings) {
    if (specificType) {
        return specificType;
    }
    if (chartType === "line" && (settings === null || settings === void 0 ? void 0 : settings.areaDefault)) {
        return "line";
    }
    return chartType;
}
function resolveAxisConfigs(data) {
    var _a;
    var axisConfigs = data.axes || (_a = {},
        _a[DEFAULT_AXIS_ID] = {
            id: DEFAULT_AXIS_ID,
            position: "left",
        },
        _a);
    var entries = Object.values(axisConfigs);
    var idIndexMap = new Map();
    var sideOffsetMax = {
        left: 0,
        right: 0,
    };
    var positionCounters = {};
    var yAxis = entries.map(function (axis, index) {
        var _a, _b;
        idIndexMap.set(axis.id, index);
        var position = (_a = axis.position) !== null && _a !== void 0 ? _a : (index === 0 ? "left" : "right");
        var counter = (_b = positionCounters[position]) !== null && _b !== void 0 ? _b : 0;
        positionCounters[position] = counter + 1;
        var offsetValue = position === "left" || position === "right"
            ? counter * AXIS_OFFSET_STEP
            : 0;
        if ((position === "left" || position === "right") &&
            offsetValue > sideOffsetMax[position]) {
            sideOffsetMax[position] = offsetValue;
        }
        var axisLine = {};
        if (axis.showAxisLine !== undefined) {
            axisLine.show = axis.showAxisLine;
        }
        var splitLine = {
            show: false, // 默认关闭分割线
        };
        return {
            id: axis.id,
            type: "value",
            position: position,
            name: axis.name,
            // 如果未设置 min，默认使用 "dataMin" 以自动根据数据调整起始坐标，避免下方空白
            min: axis.min !== undefined ? axis.min : "dataMin",
            // 如果未设置 max，默认使用 "dataMax" 以自动根据数据调整结束坐标
            max: axis.max !== undefined ? axis.max : "dataMax",
            splitNumber: axis.splitNumber,
            axisLabel: axis.axisLabel && axis.axisLabel.formatter
                ? { formatter: axis.axisLabel.formatter }
                : undefined,
            axisLine: Object.keys(axisLine).length > 0 ? axisLine : undefined,
            splitLine: Object.keys(splitLine).length > 0 ? splitLine : undefined,
            offset: offsetValue > 0 ? offsetValue : undefined,
        };
    });
    return { yAxis: yAxis, idIndexMap: idIndexMap, sideOffsetMax: sideOffsetMax };
}
function attachAxisBoundsByCategorySeries(yAxis, seriesConfig) {
    var axisValueMap = new Map();
    seriesConfig.forEach(function (series) {
        var _a, _b;
        var axisId = (_a = series.axisId) !== null && _a !== void 0 ? _a : DEFAULT_AXIS_ID;
        var values = (series.data || []).filter(function (value) { return typeof value === "number"; });
        if (!axisValueMap.has(axisId)) {
            axisValueMap.set(axisId, []);
        }
        if (values.length) {
            (_b = axisValueMap.get(axisId)) === null || _b === void 0 ? void 0 : _b.push.apply(_b, values);
        }
    });
    return yAxis.map(function (axis) {
        var _a;
        // 如果已经设置了 min 或 max（且不是 "dataMin"/"dataMax"），或者没有数据，直接返回
        if ((axis.min !== undefined && axis.min !== null && axis.min !== "dataMin") ||
            (axis.max !== undefined && axis.max !== null && axis.max !== "dataMax") ||
            !axisValueMap.has(axis.id)) {
            return axis;
        }
        var values = (_a = axisValueMap.get(axis.id)) !== null && _a !== void 0 ? _a : [];
        if (!values.length) {
            return axis;
        }
        // 只有当 min/max 未设置或为 "dataMin"/"dataMax" 时才使用自动计算
        // 保持使用 "dataMin"/"dataMax"，让 ECharts 自动处理，避免下方空白
        return __assign(__assign({}, axis), { min: (axis.min === undefined || axis.min === null || axis.min === "dataMin") ? "dataMin" : axis.min, max: (axis.max === undefined || axis.max === null || axis.max === "dataMax") ? "dataMax" : axis.max });
    });
}
function buildSeries(ctx, settings) {
    var data = ctx.data, chartType = ctx.chartType;
    var _a = resolveAxisConfigs(data), yAxis = _a.yAxis, idIndexMap = _a.idIndexMap;
    var series = data.series.map(function (seriesItem, seriesIndex) {
        var _a, _b, _c, _d;
        var type = pickSeriesType(seriesItem.type, chartType, settings);
        var axisId = (_a = seriesItem.axisId) !== null && _a !== void 0 ? _a : DEFAULT_AXIS_ID;
        var yAxisIndex = (_b = idIndexMap.get(axisId)) !== null && _b !== void 0 ? _b : 0;
        var resolvedColor = (_c = seriesItem.color) !== null && _c !== void 0 ? _c : DEFAULT_COLORS[seriesIndex % DEFAULT_COLORS.length];
        return {
            id: seriesItem.id,
            name: seriesItem.name,
            type: type,
            data: seriesItem.data,
            yAxisIndex: yAxisIndex,
            smooth: seriesItem.smooth !== undefined
                ? seriesItem.smooth
                : settings.smoothDefault,
            stack: seriesItem.stack,
            areaStyle: type === "line" && ((_d = seriesItem.area) !== null && _d !== void 0 ? _d : settings.areaDefault)
                ? {}
                : undefined,
            showSymbol: seriesItem.showSymbol !== undefined
                ? seriesItem.showSymbol
                : settings.showSymbolDefault,
            color: resolvedColor,
            itemStyle: type === "bar"
                ? { color: resolvedColor }
                : { color: resolvedColor },
            lineStyle: type === "line"
                ? { color: resolvedColor, width: 2 }
                : undefined,
            barWidth: type === "bar" ? seriesItem.barWidth : undefined,
            emphasis: { focus: "series" },
        };
    });
    return { series: series, yAxis: yAxis };
}
function buildCategoryOption(ctx) {
    var _a, _b, _c, _d;
    var mergedSettings = mergeCategorySettings(ctx.settings);
    var data = ctx.data;
    var _e = buildSeries(ctx, mergedSettings), series = _e.series, yAxis = _e.yAxis;
    var axisColorByIndex = new Map();
    var resolvedYAxis = yAxis;
    if (mergedSettings.synchronizeAxisBounds) {
        resolvedYAxis = attachAxisBoundsByCategorySeries(yAxis, data.series);
    }
    series.forEach(function (item) {
        var _a;
        var color = typeof item.color === "string" ? item.color : undefined;
        if (!color) {
            return;
        }
        var index = (_a = item.yAxisIndex) !== null && _a !== void 0 ? _a : 0;
        var existing = axisColorByIndex.get(index);
        if (!existing || (item.type === "bar" && !existing.isBar)) {
            axisColorByIndex.set(index, { color: color, isBar: item.type === "bar" });
        }
    });
    if (!axisColorByIndex.has(0) && series.length > 0) {
        var first = series[0];
        var fallbackColor = typeof first.color === "string" ? first.color : DEFAULT_COLORS[0];
        axisColorByIndex.set(0, {
            color: fallbackColor,
            isBar: first.type === "bar",
        });
    }
    var decoratedYAxis = resolvedYAxis.map(function (axis, index) {
        var _a, _b, _c, _d, _e, _f;
        // 如果 settings 中设置了 yAxisShow: false，直接关闭所有 Y 轴元素
        if (mergedSettings.yAxisShow === false) {
            return __assign(__assign({}, axis), { show: false, axisLine: { show: false }, axisLabel: { show: false }, axisTick: { show: false }, splitLine: { show: false } });
        }
        var hint = (_a = axisColorByIndex.get(index)) !== null && _a !== void 0 ? _a : (index < DEFAULT_COLORS.length
            ? { color: DEFAULT_COLORS[index], isBar: false }
            : undefined);
        var axisColor = hint === null || hint === void 0 ? void 0 : hint.color;
        if (!axisColor) {
            return axis;
        }
        var axisWithColor = __assign({}, axis);
        axisWithColor.axisLine = __assign(__assign({}, (axisWithColor.axisLine || {})), { show: ((_b = axisWithColor.axisLine) === null || _b === void 0 ? void 0 : _b.show) !== false, lineStyle: __assign(__assign({}, (((_c = axisWithColor.axisLine) === null || _c === void 0 ? void 0 : _c.lineStyle) || {})), { color: axisColor }) });
        axisWithColor.axisTick = __assign(__assign({}, (axisWithColor.axisTick || {})), { show: ((_d = axisWithColor.axisLine) === null || _d === void 0 ? void 0 : _d.show) !== false, length: 12, inside: true, lineStyle: { color: axisColor, width: 2 } });
        axisWithColor.axisLabel = __assign(__assign({}, (axisWithColor.axisLabel || {})), { color: axisColor });
        axisWithColor.nameTextStyle = __assign(__assign({}, (axisWithColor.nameTextStyle || {})), { color: axisColor });
        if ((_e = axisWithColor.splitLine) === null || _e === void 0 ? void 0 : _e.show) {
            var rgba = hexToRgba(axisColor, 0.25);
            axisWithColor.splitLine = __assign(__assign({}, axisWithColor.splitLine), { lineStyle: __assign(__assign({}, (((_f = axisWithColor.splitLine) === null || _f === void 0 ? void 0 : _f.lineStyle) || {})), { color: rgba !== null && rgba !== void 0 ? rgba : axisColor }) });
        }
        return axisWithColor;
    });
    // 根据图例位置计算 grid 偏移量
    var legendConfig = buildLegend(data.legend, mergedSettings.legend);
    var legendPosition = legendConfig.show === false
        ? null
        : ((_d = (_b = (_a = mergedSettings.legend) === null || _a === void 0 ? void 0 : _a.position) !== null && _b !== void 0 ? _b : (_c = data.legend) === null || _c === void 0 ? void 0 : _c.position) !== null && _d !== void 0 ? _d : "top");
    var LEGEND_MARGIN = 16; // 图例离外边距
    var LEGEND_GAP_HORIZONTAL = 24; // 图例在上下时，与绘制区之间的间距
    var LEGEND_GAP_VERTICAL = 16; // 图例在左右时，与绘制区之间的间距
    var LEGEND_HEIGHT = 24; // 水平图例的估算高度
    var LEGEND_WIDTH = 60; // 垂直图例的估算宽度
    var BASE_MARGIN = 16; // 绘制区到另一边的基础边距
    // 计算 grid 偏移量（已删除所有原有内边距）
    // 图例在上：top = 图例外边距(16) + 图例高度(24) + 间距(24) = 64, bottom = 16, left = 16, right = 16
    // 图例在下：top = 16, bottom = 间距(24) + 图例高度(24) + 图例外边距(16) = 64, left = 16, right = 16
    // 图例在左：top = 16, bottom = 16, left = 图例外边距(16) + 图例宽度(60) + 间距(16) = 92, right = 16
    // 图例在右：top = 16, bottom = 16, left = 16, right = 间距(16) + 图例宽度(60) + 图例外边距(16) = 92
    var gridTop;
    if (legendPosition === "top") {
        gridTop = LEGEND_MARGIN + LEGEND_HEIGHT + LEGEND_GAP_HORIZONTAL;
    }
    else if (legendPosition === "bottom") {
        gridTop = BASE_MARGIN;
    }
    else if (legendPosition === "left" || legendPosition === "right") {
        // 图例在左右时，上下边距为16
        gridTop = BASE_MARGIN;
    }
    else {
        gridTop = 40;
    }
    var gridBottom;
    if (legendPosition === "bottom") {
        gridBottom = LEGEND_GAP_HORIZONTAL + LEGEND_HEIGHT + LEGEND_MARGIN;
    }
    else if (legendPosition === "top") {
        gridBottom = BASE_MARGIN;
    }
    else if (legendPosition === "left" || legendPosition === "right") {
        // 图例在左右时，上下边距为16
        gridBottom = BASE_MARGIN;
    }
    else {
        gridBottom = 40;
    }
    var gridLeft;
    var gridRight;
    if (legendPosition === "top" || legendPosition === "bottom") {
        // 图例在上或下时，左右边距为16（删除所有原有内边距）
        gridLeft = BASE_MARGIN;
        gridRight = BASE_MARGIN;
    }
    else {
        // 图例在左右时，删除原有内边距，只保留图例相关空间
        gridLeft = (legendPosition === "left" ? LEGEND_MARGIN + LEGEND_WIDTH + LEGEND_GAP_VERTICAL : BASE_MARGIN);
        gridRight = (legendPosition === "right" ? LEGEND_GAP_VERTICAL + LEGEND_WIDTH + LEGEND_MARGIN : BASE_MARGIN);
    }
    // 从 settings 中读取 xAxis 配置（如果存在）
    var settingsXAxis = mergedSettings.xAxis;
    var shouldShowXAxis = mergedSettings.xAxisShow !== false;
    // 构建基础 x 轴配置
    var baseXAxis = {
        type: "category",
        data: data.categories,
        boundaryGap: series.some(function (s) { return s.type === "bar"; }),
        show: shouldShowXAxis,
        splitLine: { show: false }, // 关闭 X 轴分割线
    };
    // 如果 x 轴应该显示，设置默认的 axisLine、axisLabel、axisTick
    if (shouldShowXAxis) {
        baseXAxis.axisLine = undefined;
        baseXAxis.axisLabel = undefined;
        baseXAxis.axisTick = undefined;
    }
    else {
        baseXAxis.axisLine = { show: false };
        baseXAxis.axisLabel = { show: false };
        baseXAxis.axisTick = { show: false };
    }
    // 如果 settings 中有 xAxis 配置，合并到基础配置中
    // 注意：xAxis.data 始终使用 data.categories（数据源），而不是 settings 中的 data
    var finalXAxis = settingsXAxis
        ? [
            __assign(__assign(__assign({}, baseXAxis), settingsXAxis), { 
                // 始终使用 data.categories 作为 x 轴数据源
                data: data.categories, 
                // 深度合并 axisLabel 配置
                axisLabel: settingsXAxis.axisLabel
                    ? __assign(__assign({}, (baseXAxis.axisLabel || {})), settingsXAxis.axisLabel) : baseXAxis.axisLabel, 
                // 深度合并 axisLine 配置
                axisLine: settingsXAxis.axisLine
                    ? __assign(__assign({}, (baseXAxis.axisLine || {})), settingsXAxis.axisLine) : baseXAxis.axisLine, 
                // 深度合并 axisTick 配置
                axisTick: settingsXAxis.axisTick
                    ? __assign(__assign({}, (baseXAxis.axisTick || {})), settingsXAxis.axisTick) : baseXAxis.axisTick }),
        ]
        : [baseXAxis];
    var option = {
        textStyle: {
            fontFamily: '"Noto Sans SC", "PingFang SC", "Microsoft YaHei", "Helvetica Neue", sans-serif',
        },
        grid: {
            top: gridTop,
            left: gridLeft,
            right: gridRight,
            bottom: gridBottom,
            containLabel: true,
        },
        tooltip: buildTooltip(data.tooltip, mergedSettings.tooltip),
        legend: legendConfig,
        xAxis: finalXAxis,
        yAxis: decoratedYAxis,
        series: series,
    };
    var dataZoom = buildDataZoom(data.dataZoom, mergedSettings.dataZoom);
    if (dataZoom) {
        option.dataZoom = dataZoom;
    }
    return option;
}
export function createCategoryChartAdapter(chartType) {
    return {
        type: chartType,
        buildOption: function (ctx) {
            return buildCategoryOption(__assign(__assign({}, ctx), { chartType: chartType }));
        },
        getDefaultSettings: function () { return ({
            legend: { show: true },
            tooltip: { enable: true, trigger: "axis" },
            dataZoom: { enable: false, start: 0, end: 100 },
            areaDefault: false,
            smoothDefault: chartType === "line",
            showSymbolDefault: chartType === "line",
            synchronizeAxisBounds: false,
        }); },
    };
}
